import { useEffect } from 'react';
import { QueryFunctionContext, useQuery, useQueryClient } from 'react-query';
import surveyService, { AppointmentTask } from '../services/survey.service';

// Shared cache key prefix — the hook below filters by both clinic
// path and appointment id, but invalidations always run against the
// prefix so the next consumer re-fetches regardless of which
// appointment/path it's keyed under.
const APPOINTMENT_SURVEYS_KEY = 'appointmentSurveys';

const getQuery = () => {
  return async (context: QueryFunctionContext) => {
    const path = context.queryKey[1] as string;
    const appointmentId = context.queryKey[2] as string;

    if (!path || !appointmentId) return { items: [] as AppointmentTask[] };

    return surveyService.listForAppointment(path, appointmentId);
  };
};

/**
 * All pre-visit tasks attached to a single appointment — surveys,
 * consents, and the request-level items (photo ID, insurance card,
 * insurance, basic information). One DDB-fanout query per fetch, so we
 * only call this for the patient's NEXT appointment to keep cost in
 * check. Skipped when either parameter is missing.
 */
const useAppointmentSurveys = (path?: string | null, appointmentId?: string | null) => {
  const enabled = !!path && !!appointmentId;
  const queryKey = [APPOINTMENT_SURVEYS_KEY, path || null, appointmentId || null] as const;

  const { data, isFetching, refetch } = useQuery<{ items: AppointmentTask[] }>(
    queryKey,
    getQuery(),
    {
      enabled,
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 30 * 1000,
    }
  );

  return {
    items: data?.items ?? [],
    isFetching,
    refetch,
  };
};

export default useAppointmentSurveys;

// Invalidate the home pre-visit-tasks cache when the calling component
// unmounts. The task runners (UploadRunner, RequestFormRunner,
// SurveyRunner, ConsentRunner) all sit behind links from Home and may
// flip a task's `completedAt` server-side without notifying the cache.
// Without this, React Query serves the stale list for `staleTime` (30s)
// after the user navigates back, so a just-completed Photo ID still
// reads "not started" until the cache expires or the user refreshes.
//
// Invalidating on unmount catches every exit path — the runner's
// own "Back to home" / Continue button, the inline back chevron link,
// or browser back — without each runner having to wire it up
// explicitly.
export const useInvalidateAppointmentSurveysOnUnmount = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    return () => {
      queryClient.invalidateQueries([APPOINTMENT_SURVEYS_KEY]);
    };
  }, [queryClient]);
};
